import {
  AccusedAdvocate,
  AccusedProsecutor,
  AccusedSummon,
  AdminUnit,
  AdvocateCollectedEvidence,
  AdvocateComplaints,
  Appeal,
  AssignedProsecutor,
  CollectedVictim,
  Complaints,
  CouncilLitigation,
  CourtAppeal,
  DocumentEvidence,
  Investigation,
  InvestigationComment,
  Litigation,
  Prosecutor,
  Summon,
} from "types";
import apiClient from "../lib/apiClients";

export const fetchProsecutor = async () => {
  const response = await apiClient.get<Prosecutor[]>(
    "/prosecutor-decipiline/prosecutors/"
  );
  return response.data;
};

export const fetchAssignedProsecutor = async (
  adminUnit: string,
  orgUnit: string,
  token: string
) => {
  const url = `/prosecutor-decipiline/complaints/fetch-employees-by-org/`;
  const response = await apiClient.get<AssignedProsecutor[]>(
    `${url}?admin_id=${adminUnit}&org_id=${orgUnit}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const fetchAdminUnitDetail = async (
  adminUnit: string,
  token: string
) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL_2;
  const url = `${baseUrl}organization/administration-structures/${adminUnit}`;
  const response = await apiClient.get<AdminUnit>(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const fetchChildAdminUnits = async (
  adminUnit: string,
  token: string
) => {
  const response = await apiClient.get<any>(
    `advocate_decipiline/advocate-complaints/child-admin-units/?parent_admin_unit_uuid=${adminUnit}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const fetchRe_AssignedProsecutor = async (
  adminUnit: string,
  orgUnit: string,
  token: string
) => {
  const url = `/prosecutor-decipiline/litigation-verdict-prosecutor/fetch-employees-by-org/`;
  const response = await apiClient.get<AssignedProsecutor[]>(
    `${url}?admin_id=${adminUnit}&org_id=${orgUnit}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const fetchAccusedProsecutor = async (
  name: string,
  woreda: string = "",
  zone: string = ""
) => {
  const response = await apiClient.get<AccusedProsecutor[]>(
    `/prosecutor-decipiline/complaints/search-employee/?name=${name}&wereda=${woreda}&zone=${zone}`
  );
  return response.data?.map((obj) => ({
    uu_id: obj?.uu_id,
    english_first_name: obj?.english_first_name,
    english_middle_name: obj?.english_middle_name,
    english_last_name: obj?.english_last_name,
    full_name: `${obj?.english_first_name} ${obj?.english_middle_name} ${obj?.english_last_name}`,
    employee_type: obj?.employee_type,
    status: obj?.status,
  }));
};

export const fetchAdvocateComplaintLogs = async () => {
  const response = await apiClient.get<any[]>("/advocate_decipiline/logs/");
  return response.data;
};
export const fetchAdvocateLogsByComplaintId = async (id: string | any) => {
  const response = await apiClient.get<any[]>(
    `/advocate_decipiline/logs/by-complaint/${id}`
  );
  return response.data;
};

export const fetchAdvocateLogsByInvestigationId = async (id: string | any) => {
  const response = await apiClient.get<any[]>(
    `/advocate_decipiline/logs/by-investigation/${id}`
  );
  return response.data;
};
export const fetchAdvocateLogsByAppealId = async (id: string | any) => {
  const response = await apiClient.get<any[]>(
    `/advocate_decipiline/logs/by-appeal/${id}`
  );
  return response.data;
};

export const fetchAccusedAdvocate = async (name: string) => {
  const response = await apiClient.get<AccusedAdvocate[] | any>(
    `/advocate_decipiline/advocate-complaints/search-license/?name=${name}`
  );
  return response.data?.results;
};

export const fetchOffenseTypes = async () => {
  const response = await apiClient.get<Complaints[]>(
    "/base_data/offence-types-advocate/"
  );
  return response.data;
};

export const fetchAdvocateComplaint = async () => {
  const response = await apiClient.get<Complaints[]>(
    "/advocate_decipiline/advocate-complaints/"
  );
  return response.data;
};
export const fetchAdvocateComplaintByAdminUnitId = async (
  adminUnitId: string
) => {
  const response = await apiClient.get<Complaints[]>(
    `/advocate_decipiline/advocate-complaints/filter-by-admin-unit/?admin_unit_id=${adminUnitId}`
  );
  return response.data;
};
export const fetchAdvocateComplaintByProsecutor = async (userId: string) => {
  const response = await apiClient.get<Complaints[]>(
    `/advocate_decipiline/advocate-complaints/filter-by-prosecutor/?prosecutor_id=${userId}`
  );
  return response.data;
};
export const fetchAdvocateAppealForProsecutor = async (userId: string) => {
  const response = await apiClient.get<Complaints[]>(
    `/advocate_decipiline/advocate-complaints/by-prosecutor-in-appeal/?userid_assigned_prosecutor=${userId}`
  );
  return response.data;
};
export const fetchAdvocateAccusedForProsecutor = async (userId: string) => {
  const response = await apiClient.get<Complaints[]>(
    `/advocate_decipiline/advocate-complaints/by-prosecutor-in-accused-appeal/?userid_assigned_prosecutor=${userId}`
  );
  return response.data;
};
export const fetchAdvocateCourtAppealForProsecutor = async (userId: string) => {
  const response = await apiClient.get<Complaints[]>(
    `/advocate_decipiline/advocate-complaints/by-prosecutor-in-court-appeal/?userid_assigned_prosecutor=${userId}`
  );
  return response.data;
};

export const fetchAdvocateComplaintById = async (uu_id: string) => {
  const response = await apiClient.get<Complaints[]>(
    "/advocate_decipiline/advocate-complaints/" + uu_id + "/"
  );
  return response.data;
};

export const addAdvocateComplaint = async (complaint: AdvocateComplaints) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-complaints/",
    {
      ...complaint,
    }
  );
  return response.data;
};

export const editComplaint = async (complaint: AdvocateComplaints) => {
  const response = await apiClient.put(
    "/advocate_decipiline/advocate-complaints/" + complaint?.uu_id + "/",
    complaint
  );
  return response.data;
};

export const editComplaintFormData = async (
  complaint: FormData,
  complaint_id: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/advocate_decipiline/advocate-complaints/" + complaint_id + "/",
    isFormData ? complaint : { ...complaint },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const deleteComplaint = async (complaint: Complaints) => {
  const response = await apiClient.delete(
    "/advocate_decipiline/advocate-complaints/" + complaint?.uu_id + "/"
  );
  return response.data;
};

export const addComplaintEvidence = async (
  evidence: FormData | AdvocateCollectedEvidence
) => {
  try {
    const response = await apiClient.post(
      "/advocate_decipiline/advocate-evidences/",
      evidence,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding complaint evidence:", error);
    throw error;
  }
};

export const addEvidenceAttachment = async (
  attachment: DocumentEvidence | FormData,
  isFormData: boolean = true
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-evidence-attachments/",
    isFormData ? attachment : { ...attachment },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const addComplaintVictim = async (
  victim: CollectedVictim | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-victims/",
    isFormData ? victim : { ...victim },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const addComplaintReporter = async (
  reporter: CollectedVictim | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-reporters/",
    isFormData ? reporter : { ...reporter },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const addComplaintWitness = async (
  witness: CollectedVictim | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-witnesses/",
    isFormData ? witness : { ...witness },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchInvestigation = async () => {
  const response = await apiClient.get<Investigation[]>(
    "/advocate_decipiline/advocate-investigations/"
  );
  return response.data;
};

export const fetchInvestigationByComplaintId = async (complaintId: string) => {
  const response = await apiClient.get<Investigation[]>(
    "/advocate_decipiline/advocate-complaints/" +
      complaintId +
      "/investigations/"
  );
  return response.data;
};

export const addInvestigation = async (
  investigation: Investigation | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-investigations/",
    isFormData ? investigation : { ...investigation },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editInvestigation = async (
  investigation: Investigation | FormData,
  investigation_id: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/advocate_decipiline/advocate-investigations/" + investigation_id + "/",
    isFormData ? investigation : { ...investigation },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchInvestigationFeedbackByInvestigationId = async (
  investigationId: string
) => {
  const response = await apiClient.get<InvestigationComment[]>(
    // "/advocate_decipiline/director-decision-feedbacks/" + investigationId
    "/advocate_decipiline/advocate-director-decision-feedbacks/"
  );
  return response.data;
};

export const addInvestigationFeedback = async (
  investigation: InvestigationComment | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-director-decision-feedbacks/",
    isFormData ? investigation : { ...investigation },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchChargeFeedbackByInvestigationId = async (
  investigationId: string
) => {
  const response = await apiClient.get<InvestigationComment[]>(
    // "/advocate_decipiline/director-charge-feedbacks/" + investigationId
    "/advocate_decipiline/advocate-director-charge-feedbacks/"
  );
  return response.data;
};

export const addChargeDocFeedback = async (
  investigation: InvestigationComment | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-director-charge-feedbacks/",
    isFormData ? investigation : { ...investigation },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchChargeFeedbackByCouncilByInvestigationId = async (
  investigationId: string
) => {
  const response = await apiClient.get<InvestigationComment[]>(
    // "/advocate_decipiline/councilwriter-charge-feedbacks/" + investigationId
    "/advocate_decipiline/councilwriter-charge-feedbacks/"
  );
  return response.data;
};

export const addChargeDocFeedbackByCouncil = async (
  investigation: InvestigationComment | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/councilwriter-charge-feedbacks/",
    isFormData ? investigation : { ...investigation },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchSummonByInvestigationId = async (investigationId: string) => {
  const response = await apiClient.get<Summon[]>(
    "/advocate_decipiline/advocate-investigations/" +
      investigationId +
      "/summons/"
  );
  return response.data;
};

export const addSummon = async (
  summon: Summon | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/advocate-issue-summons/",
    isFormData ? summon : { ...summon },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editSummon = async (
  summonData: Summon | FormData,
  summon_id: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/advocate_decipiline/advocate-issue-summons/" + summon_id + "/",
    isFormData ? summonData : { ...summonData },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchLitigationBySummonId = async (summonId: string) => {
  const response = await apiClient.get<Litigation[]>(
    "/advocate_decipiline/advocate-issue-summons/" + summonId + "/verdicts/"
  );
  return response.data;
};

export const addLitigation = async (
  data: Litigation | FormData | any,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/litigation-verdicts-advocate/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editLitigation = async (
  data: Litigation | FormData,
  litigationId: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/advocate_decipiline/litigation-verdicts-advocate/" + litigationId + "/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchLitigationFeedbacks = async () => {
  const response = await apiClient.get<CouncilLitigation[]>(
    "/advocate_decipiline/director-feedback-advocate/"
  );
  return response.data;
};

export const addLitigationFeedback = async (data: any) => {
  const response = await apiClient.post(
    "/advocate_decipiline/director-feedback-advocate/",
    data
  );
  return response.data;
};

export const fetchCouncilLitigationBySummonId = async (summonId: string) => {
  const response = await apiClient.get<CouncilLitigation[]>(
    "/advocate_decipiline/litigation-verdict-advocate-council-office/by-summon/" +
      summonId +
      "/"
  );
  return response.data;
};

export const addCouncilLitigation = async (
  data: CouncilLitigation | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/advocate_decipiline/litigation-verdict-advocate-council-office/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editCouncilLitigation = async (
  data: CouncilLitigation | FormData,
  litigationId: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/advocate_decipiline/counciloffice-verdicts/" + litigationId + "/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchAppealByComplaintId = async (complaintId: string) => {
  const response = await apiClient.get<Appeal[]>(
    // "/Advocate_Dicipline_Appeal/appeals/"
    "/advocate_decipiline/advocate-complaints/" +
      complaintId +
      "/appeal-requests/"
  );
  return response.data;
};

export const requestAppeal = async (
  data: FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/Advocate_Dicipline_Appeal/appeal-requests-advocate/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editAppeal = async (
  data: Appeal | FormData,
  appeal_id: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/Advocate_Dicipline_Appeal/appeal-requests-advocate/" + appeal_id + "/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchSummonByAppealId = async (appealId: string) => {
  const response = await apiClient.get<Summon[]>(
    "/Advocate_Dicipline_Appeal/appeal-requests-advocate/" +
      appealId +
      "/summons/"
  );
  return response.data;
};

export const addAppealSummon = async (
  summon: Summon | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/Advocate_Dicipline_Appeal/issue-summon-appeal-advocate/",
    isFormData ? summon : { ...summon },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editAppealSummon = async (
  summonData: Summon | FormData,
  summon_id: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/Advocate_Dicipline_Appeal/issue-summon-appeal-advocate/" +
      summon_id +
      "/",
    isFormData ? summonData : { ...summonData },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchAppealLitigationByAppealSummonId = async (
  summonId: string
) => {
  const response = await apiClient.get<Litigation[]>(
    "/Advocate_Dicipline_Appeal/appeal-litigation-verdict-prosecutor-advocate/"
    // "/Advocate_Dicipline_Appeal/appeal-litigation-verdict/by-complaint/" + summonId
  );
  return response.data;
};

export const addAppealLitigation = async (
  data: Litigation | FormData | any,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/Advocate_Dicipline_Appeal/appeal-litigation-verdict-prosecutor-advocate/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editAppealLitigation = async (
  data: Litigation | FormData,
  litigationId: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/Advocate_Dicipline_Appeal/appeal-litigation-verdict-prosecutor-advocate/" +
      litigationId +
      "/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchAppealLitigationFeedbacks = async () => {
  const response = await apiClient.get<CouncilLitigation[]>(
    "/Advocate_Dicipline_Appeal/director-feedback-advocate/"
  );
  return response.data;
};

export const addAppealLitigationFeedback = async (data: any) => {
  const response = await apiClient.post(
    "/Advocate_Dicipline_Appeal/director-feedback-advocate/",
    data
  );
  return response.data;
};

export const fetchAppealCouncilLitigationByAppealSummonId = async (
  summonId: string
) => {
  const response = await apiClient.get<CouncilLitigation[]>(
    "/Advocate_Dicipline_Appeal/appeal-litigation-verdict-council-office-advocate/"
    // "/advocate_decipiline/appeal-litigation-verdict-council-office/by-summon/" + summonId
  );
  return response.data;
};

export const addAppealCouncilLitigation = async (
  data: CouncilLitigation | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/Advocate_Dicipline_Appeal/appeal-litigation-verdict-council-office-advocate/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editAppealCouncilLitigation = async (
  data: CouncilLitigation | FormData,
  litigationId: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/Advocate_Dicipline_Appeal/appeal_litigation-verdict-council-office-advocate/" +
      litigationId +
      "/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchAccusedSummonByComplaintId = async (complaintId: string) => {
  const response = await apiClient.get<AccusedSummon[]>(
    "/Advocate_Dicipline_Appeal/issue-summon-appeal-accused-advocate/"
    // "/Advocate_Dicipline_Appeal/issue-summon-appeal-accused-advocate/by-complaint/" + complaintId
  );
  return response.data;
};

export const addAccusedSummon = async (
  summon: AccusedSummon | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/Advocate_Dicipline_Appeal/issue-summon-appeal-accused-advocate/",
    isFormData ? summon : { ...summon },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editAccusedSummon = async (
  data: AccusedSummon | FormData,
  accusedSummonId: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/Advocate_Dicipline_Appeal/issue-summon-appeal-accused-advocate/" +
      accusedSummonId +
      "/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const fetchCourtAppealByComplaintId = async (complaintId: string) => {
  const response = await apiClient.get<CourtAppeal[]>(
    // "/advocate_decipiline/appeals-to-court/"
    "/advocate_decipiline/advocate-complaints/" + complaintId + "/court-appeals"
  );
  return response.data;
};

export const addCourtAppeal = async (
  data: CourtAppeal | FormData,
  isFormData: boolean = false
) => {
  const response = await apiClient.post(
    "/Advocate_Dicipline_Appeal/appeal-from-accused-to-court/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};

export const editCourtAppeal = async (
  data: CourtAppeal | FormData,
  courtAppealId: string,
  isFormData: boolean = false
) => {
  const response = await apiClient.put(
    "/Advocate_Dicipline_Appeal/appeal-from-accused-to-court/" +
      courtAppealId +
      "/",
    isFormData ? data : { ...data },
    isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}
  );
  return response.data;
};
